import { type FC, useEffect, useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery, useFragment, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SeoLink, ALWAYS_LINKABLE_LINK } from 'dibs-elements/exports/SeoLink.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import LazyImage from 'dibs-react-lazy-image';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import { getUserEventItem, trackUserEventItemAddToFavorites } from 'dibs-tracking';
import { trackModule } from '../helpers/moduleLocationTracking.ts';
import {
    trackNewArrivalsViewMoreClick,
    trackNewArrivalsArrowClick,
    trackNewArrivalsInitialDisplay,
    trackNewArrivalsItemClick,
    trackNewArrivalsItemImpression,
} from '../helpers/hpRvNewArrivalsTracking.ts';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper.js';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { getBuyerId } from 'dibs-cookie-jar';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';

import styles from './HpNewArrivalsCarousel.scss';

import { type HpNewArrivalsCarouselQuery } from './__generated__/HpNewArrivalsCarouselQuery.graphql.ts';
import { type HpNewArrivalsCarousel_item$key } from './__generated__/HpNewArrivalsCarousel_item.graphql.ts';
import { type HpNewArrivalsCarousel_viewer$key } from './__generated__/HpNewArrivalsCarousel_viewer.graphql.ts';

const ITEMS_PER_PAGE = 6;
const ITEMS_PER_PAGE_MOBILE = 2.5;
const TOTAL_ITEMS = 12;
const TRACKING_TITLE = 'New Arrivals';
const NEW_ARRIVALS_PATH = '/new-arrivals/?sort=newest';

const newArrivalsQuery = graphql`
    query HpNewArrivalsCarouselQuery($first: Int!, $uriRef: String!, $userId: String!) {
        viewer {
            ...HpNewArrivalsCarousel_viewer @arguments(userId: $userId)
            itemSearch(first: $first, uriRef: $uriRef) {
                edges {
                    node {
                        item {
                            serviceId
                            title
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            ...HpNewArrivalsCarousel_item @arguments(userId: $userId)
                        }
                    }
                }
            }
        }
    }
`;

const viewerFragment = graphql`
    fragment HpNewArrivalsCarousel_viewer on Viewer
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        ...ItemHeartWrapper_viewer @arguments(userIds: [$userId], loadPortfolioData: true)
    }
`;

const itemFragment = graphql`
    fragment HpNewArrivalsCarousel_item on Item
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        serviceId
        title
        pdpURL
        firstPhotoWebPath(size: small)
        ecommerceTrackingParams
        browseUrl
        categoryCode
        contemporaryTrackingString
        seller {
            serviceId
        }
        ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: true)
    }
`;

type ItemTileProps = {
    itemRef: HpNewArrivalsCarousel_item$key;
    viewerRef: HpNewArrivalsCarousel_viewer$key;
    index: number;
    userId: string;
};

const ItemTile: FC<ItemTileProps> = ({ itemRef, viewerRef, index, userId }) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [isLoginFlowInProgress, setIsLoginFlowInProgress] = useState(false);

    const lazyClassName = classnames(
        dibsCss.wFull,
        dibsCss.hFull,
        dibsCss.flex,
        dibsCss.itemsCenter,
        dibsCss.justifyCenter
    );

    const trackingItem = {
        itemId: item.serviceId || '',
        itemTitle: item.title || '',
        brand: item.seller?.serviceId,
        category: item.browseUrl,
        categoryCode: item.categoryCode,
        contemporaryTrackingString: item.contemporaryTrackingString,
    };

    const handleClick = (): void => {
        trackNewArrivalsItemClick({ item: trackingItem, index });
        trackModule(TRACKING_TITLE);
    };

    const handleContentVisible = (): void => {
        trackNewArrivalsItemImpression({ item: trackingItem, index });
    };

    const heartAuthModalShow = useCallback(() => {
        authModalLoader
            ?.show({
                flow: authModalLoader?.constants?.SAVE_ITEM_FLOW,
            })
            .then(() => {
                setIsLoginFlowInProgress(true);
            });
    }, []);

    const handleFavorited = useCallback(() => {
        const userEventItem = getUserEventItem({
            id: item.serviceId,
            amount: item.ecommerceTrackingParams?.price,
            convertedAmounts: item.ecommerceTrackingParams?.convertedAmounts,
        });
        if (userEventItem) {
            trackUserEventItemAddToFavorites(environment, [userEventItem]);
        }
    }, [environment, item.serviceId, item.ecommerceTrackingParams]);

    return (
        <div
            className={classnames(
                dibsCss.wFull,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter
            )}
        >
            <div
                className={classnames(
                    styles.itemInner,
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.itemsStart,
                    dibsCss.justifyEnd,
                    dibsCss.ptXsmall,
                    dibsCss.bgWhite,
                    dibsCss.overflowVisible
                )}
                data-tn="new-arrivals-tile"
            >
                <div className={classnames(dibsCss.flex1, dibsCss.selfStretch, dibsCss.minW0)}>
                    <Link
                        className={classnames(dibsCss.block, dibsCss.wFull, dibsCss.hFull)}
                        href={item.pdpURL || ''}
                        dataTn="new-arrivals-item"
                        onClick={handleClick}
                    >
                        <LazyImage
                            className={lazyClassName}
                            placeholderClass={lazyClassName}
                            onContentVisible={handleContentVisible}
                        >
                            <img
                                src={item.firstPhotoWebPath || ''}
                                className={classnames(
                                    dibsCss.wFull,
                                    dibsCss.hFull,
                                    dibsCss.objectContain
                                )}
                                alt={item.title || ''}
                                sizes="113px"
                                srcSet={getSrcsetString(item.firstPhotoWebPath || '')}
                            />
                        </LazyImage>
                    </Link>
                </div>
                <div
                    className={classnames(
                        styles.heartWrapper,
                        dibsCss.relative,
                        dibsCss.flexShrink0,
                        dibsCss.overflowVisible
                    )}
                >
                    <ItemHeartWrapper
                        theme="dark"
                        itemId={item.serviceId}
                        userId={userId}
                        viewer={viewer}
                        item={item}
                        authModalLoader={authModalLoader}
                        authModalShow={heartAuthModalShow}
                        onFavorited={handleFavorited}
                        isLoginFlowInProgress={isLoginFlowInProgress}
                        className={styles.heart}
                    />
                </div>
            </div>
        </div>
    );
};

type Props = {
    isMobile: boolean;
};

export const HpNewArrivalsCarousel: FC<Props> = ({ isMobile }) => {
    const isClient = useClientState();
    const userId = isClient ? getBuyerId(document.cookie) || '' : '';

    const data = useLazyLoadQuery<HpNewArrivalsCarouselQuery>(newArrivalsQuery, {
        first: TOTAL_ITEMS,
        uriRef: NEW_ARRIVALS_PATH,
        userId,
    });

    const items = data.viewer?.itemSearch?.edges || [];

    useEffect(() => {
        if (items.length) {
            trackNewArrivalsInitialDisplay({ itemsReturned: items.length });
        }
    }, [items.length]);

    if (!items.length) {
        return null;
    }

    const handleViewMoreClick = (): void => {
        trackNewArrivalsViewMoreClick();
        trackModule(TRACKING_TITLE);
    };

    const handleArrowClick = ({ direction }: { direction: string }): void => {
        trackNewArrivalsArrowClick({ direction });
    };

    const title = (
        <FormattedMessage id="HpNewArrivalsCarousel.title" defaultMessage="See What's New" />
    );

    const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE;

    return (
        <div
            className={classnames(dibsCss.pyLarge, {
                [dibsCss.pbMedium]: isMobile,
                [dibsCss.ptMedium]: isMobile,
                'primary-viewport-width': isMobile,
            })}
        >
            <div
                className={classnames(dibsCss.flex, dibsCss.wFull, {
                    [dibsCss.gapLarge]: !isMobile,
                    [dibsCss.itemsCenter]: !isMobile,
                    [dibsCss.flexCol]: isMobile,
                    [dibsCss.gapSmall]: isMobile,
                })}
            >
                <div
                    className={classnames(dibsCss.textNoir, dibsCss.flexShrink0, {
                        [dibsCss.w200px]: !isMobile,
                        [dibsCss.wFull]: isMobile,
                        [dibsCss.plSmall]: isMobile,
                    })}
                    data-tn="new-arrivals-header"
                >
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(dibsCss.mt0, {
                                    [dibsCss.sassyFontHeaderLarge]: !isMobile,
                                    [dibsCss.mbXsmall]: !isMobile,
                                    [dibsCss.sassyFontHeaderMedium]: isMobile,
                                    [dibsCss.mb0]: isMobile,
                                })}
                            >
                                {isMobile ? (
                                    <SeoLink
                                        buyerLinkType="standardLinkNoUnderline"
                                        variant={ALWAYS_LINKABLE_LINK}
                                        linkData={{ path: NEW_ARRIVALS_PATH, isLinkable: false }}
                                        onClick={handleViewMoreClick}
                                        dataTn="new-arrivals-link"
                                    >
                                        <span
                                            className={classnames(
                                                dibsCss.inlineFlex,
                                                dibsCss.itemsCenter,
                                                dibsCss.gapXxsmall
                                            )}
                                        >
                                            {title}
                                            <ChevronRight variation="24" />
                                        </span>
                                    </SeoLink>
                                ) : (
                                    title
                                )}
                            </Heading>
                        )}
                    </HeadingLevel>
                    {!isMobile && (
                        <SeoLink
                            className={dibsCss.sassyFontBody}
                            variant={ALWAYS_LINKABLE_LINK}
                            linkData={{ path: NEW_ARRIVALS_PATH, isLinkable: false }}
                            onClick={handleViewMoreClick}
                            dataTn="new-arrivals-link"
                        >
                            <FormattedMessage
                                id="HpNewArrivalsCarousel.viewAllCta"
                                defaultMessage="All New Arrivals"
                            />
                        </SeoLink>
                    )}
                </div>
                <div className={classnames(dibsCss.flex1, dibsCss.minW0, dibsCss.overflowHidden)}>
                    <ScrollCarousel
                        animation="slide"
                        enableScroll
                        itemsToShow={itemsPerPage}
                        dataTn="new-arrivals-carousel"
                        {...(isMobile
                            ? {
                                  stepSize: 1,
                                  gapSize: 'small',
                                  scrollPadding: 'small',
                              }
                            : {
                                  stepSize: ITEMS_PER_PAGE,
                                  isInfinite: true,
                                  showArrows: true,
                                  onArrowClick: handleArrowClick,
                              })}
                    >
                        {items.map((edge, index) => {
                            const item = edge?.node?.item;
                            if (!item || !data.viewer) return null;
                            return (
                                <ItemTile
                                    key={item.serviceId}
                                    itemRef={item}
                                    viewerRef={data.viewer}
                                    index={index}
                                    userId={userId}
                                />
                            );
                        })}
                    </ScrollCarousel>
                </div>
            </div>
        </div>
    );
};
